import React, { useEffect, useRef, useState } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline } from './sectionHelpers';

interface Counter { number: string | number; label: string; prefix?: string; suffix?: string; }

// Inject keyframe CSS once
const KEYFRAMES_ID = 'sb-number-counter-keyframes';
function ensureKeyframes() {
  if (typeof document !== 'undefined' && !document.getElementById(KEYFRAMES_ID)) {
    const style = document.createElement('style');
    style.id = KEYFRAMES_ID;
    style.textContent = `
      @keyframes sbCounterPop {
        from { opacity: 0; transform: scale(0.75) translateY(20px); }
        to   { opacity: 1; transform: scale(1) translateY(0); }
      }
      .sb-counter-item--visible .sb-counter-number {
        animation: sbCounterPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      }
    `;
    document.head.appendChild(style);
  }
}

const NumberCounter: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const counters = (c.counters || []) as Counter[];
  const variant = c.variant || 'light';
  const isDark = variant === 'dark';

  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    ensureKeyframes();
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="sb-render sb-render-number-counter"
      style={{
        padding: '80px 24px',
        background: isDark ? '#0f172a' : '#f8fafc',
        color: isDark ? '#fff' : '#0f172a',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Section headline */}
        {sfHeadline(c) && (
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <InlineEditText
              tag="h2"
              controlKey="headline"
              style={{ fontSize: '2.25rem', fontWeight: 800, letterSpacing: '-0.025em', color: 'inherit' }}
            >
              {sfHeadline(c)}
            </InlineEditText>
          </div>
        )}

        {/* Counters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(counters.length, 1)}, 1fr)`,
          gap: 32,
          textAlign: 'center',
        }}>
          {counters.map((ctr, i) => (
            <div
              key={i}
              className={`sb-counter-item${visible ? ' sb-counter-item--visible' : ''}`}
              style={{ padding: '16px 8px' }}
            >
              <div
                className="sb-counter-number"
                style={{
                  fontSize: '5rem',
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: '-0.04em',
                  color: 'var(--brand-primary, #2563eb)',
                  opacity: 0,
                  animationDelay: `${i * 0.12}s`,
                  marginBottom: 12,
                }}
              >
                {ctr.prefix || ''}{ctr.number || '0'}{ctr.suffix || ''}
              </div>
              <div style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--brand-text-light, #64748b)',
                letterSpacing: '0.02em',
              }}>
                {ctr.label || 'Metric'}
              </div>
            </div>
          ))}

          {counters.length === 0 && (
            <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>
              No counters configured yet.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default NumberCounter;
